import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Send, LogIn, AtSign } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import ForgotPasswordModal from './ForgotPasswordModal';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, loading: settingsLoading, settingsLoaded, safeSettings } = useSettings();

  const [showResendOption, setShowResendOption] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);

  // Center the auth card using App.css classes
  useEffect(() => {
    document.body.classList.add('auth-page');
    return () => {
      document.body.classList.remove('auth-page');
    };
  }, []);

  // Check for verification message from registration
  useEffect(() => {
    if (location.state?.verificationMessage) {
      setVerificationMessage(location.state.verificationMessage);
      // Show next steps only when the message came from registration flow
      setShowNextSteps(!!location.state?.fromRegistration);
      if (location.state?.email) {
        setEmail(location.state.email);
      }
      // Clear the state to prevent message from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResendOption(false);
    
    const result = await login(email, password);
    
    if (result.success) {
      // Route by role
      if (result.isSuperadmin || result.adminRole === 'superadmin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (result.adminRole === 'user_manager') {
        navigate('/user-manager/dashboard', { replace: true });
      } else if (result.adminRole === 'partner_admin') {
        navigate('/partner-admin/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      setError(result.error);
      // Show resend verification option for unverified users
      if (result.error && result.error.includes('verify your email')) {
        setShowResendOption(true);
      }
    }
    
    setLoading(false);
  };

  const handleResendVerification = () => {
    navigate('/verification-pending', {
      state: {
        email: email,
        message: 'Request a new verification email below.'
      }
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="text-center mb-10">
          <div className="flex justify-center items-center mb-6">
            {!settingsLoading && settings?.product_logo ? (
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl blur opacity-20"></div>
                <img 
                  src={settings.product_logo} 
                  alt={settings.product_name}
                  className="mx-auto h-16 w-auto relative z-10"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl blur opacity-20"></div>
                <div className="relative z-10 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-2xl shadow-lg">
                  <LogIn size={32} />
                </div>
              </div>
            )}
          </div>
          <div className={`app-name-container ${settingsLoaded ? 'loaded' : ''}`}>
            <h1 className="text-3xl font-bold text-gray-800 mb-3">
              {safeSettings?.product_name}
            </h1>
          </div>
          {!settingsLoaded && (
            <div className="app-name-placeholder mb-3" style={{height: '2.5rem'}}></div>
          )}
          <div className="flex items-center justify-center gap-2 mb-2">
            <AtSign size={18} className="text-blue-600" />
            <p className="text-gray-600 font-medium">Welcome back</p>
          </div>
          <p className="text-gray-500 text-sm">Sign in to your account to continue</p>
        </div>

        {/* Verification Success Message */}
        {verificationMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={16} />
              <span className="font-semibold">Check Your Email</span>
            </div>
            <p className="text-sm mb-3">{verificationMessage}</p>
            {showNextSteps && (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <h4 className="font-semibold text-sm mb-2">📧 What to do next:</h4>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>We have sent a verification mail to your inbox</li>
                  <li>Click the verification link in the email to activate your account</li>
                  <li>Return here to login to your account</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            {showResendOption && (
              <div className="mt-3 pt-3 border-t border-red-300">
                <p className="text-sm mb-2">Need to verify your email?</p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                >
                  Resend Verification Email
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Mail size={16} className="text-blue-600" />
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                placeholder="Enter your email address"
                required
              />
              <Mail className="input-icon" size={20} />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Lock size={16} className="text-blue-600" />
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input pr-12"
                placeholder="Enter your password"
                required
              />
              <Lock className="input-icon" size={20} />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors duration-200"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary w-full mt-8"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Signing in...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <LogIn size={20} />
                <span>Sign In to Account</span>
              </div>
            )}
          </button>

          {/* Forgot Password Link */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors duration-200 font-medium"
            >
              Forgot your password?
            </button>
          </div>
        </form>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
        />

        <p className="text-center mt-6 text-gray-600">
          Don't have an account?{' '}
          <Link to="/register" className="text-purple-600 hover:text-purple-500 font-medium">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;